import dayjs, { type Dayjs } from 'dayjs';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { capitalize, groupBy, keyBy, maxBy, sortBy, sumBy } from 'lodash';

import type { TimePresetKeyWithField } from '@/components/TimeFilter/TimeFilter.types';
import { parseTimeFilter } from '@/components/TimeFilter/TimeFilter.utils';
import {
  YEAR_MONTH_DAY_FORMAT,
  YEAR_MONTH_DAY_TIME_FORMAT
} from '@/constants/date.constants';
import { PRODUCTS } from '@/constants/products.constants';
import type { TicketPriorityDTO } from '@/pages/Tickets';

import { selectedOrganizationAtom } from '../Organizations';
import { getTemplateValues } from '../ReportsTemplates/MDR/ReportsTemplate.utils';
import { vulnerabilitiesTimeFilterAtom } from '../VulnerabilityManagement/VMPVulnerabilities/VMPVulnerabilities.store';
import type {
  EventsPerDayDTO,
  MyCyberScorecardDTO,
  ServiceUsagePerDayDTO,
  UsageAveragesType,
  UsageLimitType
} from './Dashboard.types';
import type { CasesMetricsGroupByDTO } from './DashboardCases/DashboardCases.types';
import { DEFAULT_MCSC_ASSESSMENT } from './dashboard.constants';

const dashboardTimeFrameAtom = atomWithStorage(
  'dashboardTimeFrame',
  30,
  undefined,
  { getOnInit: true }
);

export const ticketPrioritiesAtom = atom<TicketPriorityDTO[]>([]);

const now = dayjs();

export const usagePerDayAtom = atom<ServiceUsagePerDayDTO[]>([]);
export const usageLimitsAtom = atom<UsageLimitType[]>([]);
export const eventsPerDayAtom = atom<EventsPerDayDTO[]>([]);
export const alertsPerDayAtom = atom<EventsPerDayDTO[]>([]);
export const investigatedAlertsPerDayAtom = atom<EventsPerDayDTO[]>([]);
export const ticketedAlertsPerDayAtom = atom<EventsPerDayDTO[]>([]);
export const casesPerDayAtom = atom<CasesMetricsGroupByDTO[]>([]);

export const mcscAtom = atom<MyCyberScorecardDTO[] | null>(null);
export const selectedMCSCAssessmentIdAtom = atomWithStorage<number | null>(
  'selectedMCSCAssessmentId',
  null
);

export const refetchInternalTicketsAtom = atomWithStorage<boolean>(
  'refetchInternalTickets',
  false
);

export const selectedMCSCAssessmentAtom = atom((get) => {
  const mcscData = get(mcscAtom);
  const selectedAssessmentId = get(selectedMCSCAssessmentIdAtom);

  return (
    mcscData?.find(
      (item: MyCyberScorecardDTO) => item.assessment_id === selectedAssessmentId
    ) || DEFAULT_MCSC_ASSESSMENT
  );
});

export const latestMCSCAssessmentAtom = atom((get) => {
  const mcscData = get(mcscAtom);

  const activeAssessments = mcscData?.filter(
    (assessment: MyCyberScorecardDTO) =>
      capitalize(assessment.status) === 'Active'
  );
  const latestAssessment = sortBy(activeAssessments, 'created_at').reverse();

  return latestAssessment[0];
});

export const basicSearchParamsAtom = atom((get) => {
  const dashboardTimeFrame = get(dashboardTimeFrameAtom);
  const selectedOrg = get(selectedOrganizationAtom);

  let basicSearchParams: Record<string, string | string[]> = {
    published_at__gte: dayjs()
      .subtract(dashboardTimeFrame, 'day')
      .startOf('day')
      .format(YEAR_MONTH_DAY_TIME_FORMAT)
  };

  if (selectedOrg.id !== 0) {
    basicSearchParams = {
      ...basicSearchParams,
      organization__label: selectedOrg.label
    };
  }

  return basicSearchParams;
});

export const basicVulnerabilitiesSearchParamsAtom = atom((get) => {
  const dashboardTimeFrame = get(dashboardTimeFrameAtom);
  const vulnerabilitiesTimeFrame = get(vulnerabilitiesTimeFilterAtom);
  const selectedOrg = get(selectedOrganizationAtom);
  const pathname = window.location.pathname;

  const createTimeFrame = (timeframe: TimePresetKeyWithField | null) => {
    if (timeframe) {
      const { from } = parseTimeFilter(timeframe);
      const cTimeFilterField = 'date_identified';
      return from ? { [`${cTimeFilterField}__gte`]: from.toString() } : {};
    }
  };

  const getCreatedAtTimeFrame = () => {
    let basicParams = {};
    if (pathname.includes('dashboard')) {
      basicParams = {
        ...basicParams,
        date_identified__gte: dayjs()
          .subtract(dashboardTimeFrame, 'day')
          .startOf('day')
          .format('YYYY-MM-DD HH:mm:ss')
      };
      return basicParams;
    } else {
      basicParams = {
        ...createTimeFrame(vulnerabilitiesTimeFrame)
      };
      return basicParams;
    }
  };

  let basicSearchParams = getCreatedAtTimeFrame();

  if (selectedOrg.id !== 0) {
    basicSearchParams = {
      ...basicSearchParams,
      organization__label: selectedOrg.label
    };
  }

  return basicSearchParams;
});

export const ticketSearchParamsAtom = atom((get) => {
  const selectedOrg = get(selectedOrganizationAtom);

  let ticketSearchParams: Record<string, string | string[]> = {
    published_at__gte: dayjs()
      .subtract(180, 'day')
      .startOf('day')
      .format(YEAR_MONTH_DAY_TIME_FORMAT)
  };

  if (selectedOrg.id !== 0) {
    ticketSearchParams = {
      ...ticketSearchParams,
      organization__label: selectedOrg.label
    };
  }

  return ticketSearchParams;
});

// Search params to get the open ticket count
export const openTicketSearchParamsAtom = atom((get) => {
  const selectedOrg = get(selectedOrganizationAtom);

  let ticketSearchParams: Record<string, string | string[]> = {
    published_at__gte: dayjs()
      .subtract(180, 'day')
      .startOf('day')
      .format(YEAR_MONTH_DAY_TIME_FORMAT)
  };

  if (selectedOrg.id !== 0) {
    ticketSearchParams = {
      ...ticketSearchParams,
      organization__label: selectedOrg.label
    };
  }
  return ticketSearchParams;
});

// filter the events by date based on the time frame and combine log and net events for each day
export const alertsByDateAtom = atom((get) => {
  const alertsPerDay = get(alertsPerDayAtom);
  const dashboardTimeFrame = get(dashboardTimeFrameAtom);
  const alerts = [];

  const { pathname } = window.location;

  let timeFrame: number;
  let endDate: Dayjs;

  if (pathname === '/mdr-reports-template') {
    const diff = getTemplateValues();
    timeFrame = diff;
    endDate = dayjs(new URLSearchParams(window.location.search).get('end')); // Get startDate from URL
  } else {
    timeFrame = dashboardTimeFrame;
    endDate = dayjs().subtract(1, 'day');
  }

  // Create a map of existing data by date
  const alertsMap = new Map();
  alertsPerDay?.forEach((item) => {
    if (item.date) {
      const dateKey = dayjs(item.date).format('YYYY-MM-DD');
      // Accumulate counts for the same date (handles multiple alert types per day)
      const existingCount = alertsMap.get(dateKey) || 0;
      alertsMap.set(dateKey, existingCount + (item.count || 0));
    }
  });

  // make sure every day is represented in the array by looping through the time frame and checking if there is a record for that day
  for (let i = 0; i <= timeFrame; i += 1) {
    const date = `${endDate
      .subtract(timeFrame - i, 'days')
      .format(YEAR_MONTH_DAY_FORMAT)}`;

    alerts.push({
      date: dayjs(date).toDate(),
      alerts: alertsMap.get(date) || 0
    });
  }

  return alerts;
});

// filter the events by date based on the time frame and combine log and net events for each day
export const eventsByDateAtom = atom((get) => {
  const eventsPerDay = get(eventsPerDayAtom);
  const dashboardTimeFrame = get(dashboardTimeFrameAtom);
  const groupedEvents = groupBy(eventsPerDay, 'date');
  const events = [];

  const { pathname } = window.location;
  let timeFrame: number;
  let endDate: Dayjs;

  if (pathname === '/mdr-reports-template') {
    const diff = getTemplateValues();
    timeFrame = diff;
    endDate = dayjs(new URLSearchParams(window.location.search).get('end')); // Get startDate from URL
  } else {
    timeFrame = dashboardTimeFrame;
    endDate = dayjs().subtract(1, 'day');
  }

  // make sure every day is represented in the array by looping through the time frame and checking if there is a record for that day
  for (let i = 0; i <= timeFrame; i += 1) {
    const date = `${endDate
      .subtract(timeFrame - i, 'days')
      .format(YEAR_MONTH_DAY_FORMAT)}`;

    // groupedEvents[date] is an array and contains log and net events for that day
    const count = groupedEvents[date] ? sumBy(groupedEvents[date], 'count') : 0;

    events.push(count);
  }
  return events;
});

export const casesEventsByDateAtom = atom((get) => {
  const eventsPerDay = get(eventsPerDayAtom);
  const casesPerDay = get(casesPerDayAtom);
  const dashboardTimeFrame = get(dashboardTimeFrameAtom);
  const alertsEventsData = [];

  const { pathname } = window.location;

  let timeFrame: number;
  let endDate: Dayjs;

  if (pathname === '/mdr-reports-template') {
    const diff = getTemplateValues();
    timeFrame = diff;
    endDate = dayjs(new URLSearchParams(window.location.search).get('end')); // Get startDate from URL
  } else {
    timeFrame = dashboardTimeFrame;
    endDate = dayjs().subtract(1, 'day');
  }

  // Create a map of existing data by date
  const casesMap = new Map();
  casesPerDay.forEach((item) => {
    if (item.value) {
      const dateKey = dayjs(item.value).format(YEAR_MONTH_DAY_FORMAT);
      // Accumulate counts for the same date (handles multiple alert types per day)
      casesMap.set(dateKey, (casesMap.get(dateKey) || 0) + (item.count || 0));
    }
  });

  const averageCases = Math.round(
    sumBy(
      casesPerDay.filter((item) => item.value !== 'Null'),
      'count'
    ) /
      (dashboardTimeFrame + 1)
  );

  const eventsMap = new Map();
  eventsPerDay?.forEach((item) => {
    if (item.date) {
      const dateKey = dayjs(item.date).format(YEAR_MONTH_DAY_FORMAT);
      // Accumulate counts for the same date (handles multiple event types per day)
      const existingCount = eventsMap.get(dateKey) || 0;
      eventsMap.set(dateKey, existingCount + (item.count || 0));
    }
  });

  for (let i = 0; i <= timeFrame; i += 1) {
    const date = `${endDate
      .subtract(timeFrame - i, 'days')
      .format(YEAR_MONTH_DAY_FORMAT)}`;

    alertsEventsData.push({
      date: dayjs(date).toDate(),
      cases: casesMap.get(date) || 0,
      events: eventsMap.get(date) || 0,
      averageCases // Include the average number of cases per day for the dashboard cases page, will be ignored everywhere else
    });
  }

  return alertsEventsData;
});

// calculate the average log and network usages over the time frame
export const usageAverageAtom = atom((get) => {
  const timeFrame = get(dashboardTimeFrameAtom);
  const usage = get(usagePerDayAtom);
  const filteredUsage = usage.filter((row) =>
    dayjs(row.date).isSameOrAfter(now.subtract(timeFrame, 'days'), 'day')
  );

  if (filteredUsage.length !== 0) {
    const logAverage =
      filteredUsage
        .filter((row) => row.type === 'log')
        .reduce((acc, curr) => acc + curr.gb, 0) / timeFrame;

    const networkAverage =
      filteredUsage
        .filter((row) => row.type === 'net')
        .reduce((acc, curr) => acc + curr.mb_per_second, 0) / timeFrame;

    const decimalPlaces = 2;
    const decimalFactor = 10 ** decimalPlaces;

    // Rounding with Ceil will at least show data for anything above 0, 0 data will still be 0
    const usageAverages: UsageAveragesType = {
      log: Math.ceil(logAverage * decimalFactor) / decimalFactor,
      network: Math.ceil(networkAverage * decimalFactor) / decimalFactor
    };

    return usageAverages;
  }

  return { log: 0, network: 0 };
});

// find the log usage limit
export const logUsageLimitsAtom = atom((get) => {
  const usageLimits = get(usageLimitsAtom);

  const logLimit = usageLimits.filter(
    (item: UsageLimitType) => item.code === 'POND-LOG' && item.active // Checking for code because multiple products have product type LOG
  );

  if (logLimit.length > 0) {
    return maxBy(logLimit, 'subscription_limit')?.subscription_limit ?? null;
  }
  return null;
});

// find the network usage limit
export const networkUsageLimitsAtom = atom((get) => {
  const usageLimits = get(usageLimitsAtom);

  const networkLimit = usageLimits.filter(
    (item: UsageLimitType) => item.product_type === PRODUCTS.NET && item.active
  );

  if (networkLimit.length > 0) {
    return (
      maxBy(networkLimit, 'subscription_limit')?.subscription_limit ?? null
    );
  }
  return null;
});

// create an array of log usage data ensuring every day is represented
export const logUsageByDateAtom = atom((get) => {
  const timeFrame = get(dashboardTimeFrameAtom);
  const usagePerDay = get(usagePerDayAtom);
  const logUsagePerDay = keyBy(
    usagePerDay.filter((r) => r.type === 'log'),
    'date'
  );

  // Start previous day as metrics are not fully calculated until the day after
  const startDate = dayjs().subtract(1, 'day');
  const usage: { [key: string]: string | number | Date }[] = [];
  for (let i = 0; i <= timeFrame; i += 1) {
    const date = `${startDate
      .subtract(timeFrame - i, 'days')
      .format(YEAR_MONTH_DAY_FORMAT)}`;

    usage.push({
      date: dayjs(date).toDate(),
      usage: logUsagePerDay[date] ? logUsagePerDay[date]?.gb : 0
    });
  }

  return usage;
});

// create an array of network usage data ensuring every day is represented
export const networkUsageByDateAtom = atom((get) => {
  const timeFrame = get(dashboardTimeFrameAtom);
  const usagePerDay = get(usagePerDayAtom);
  const netUsagePerDay = keyBy(
    usagePerDay.filter((r) => r.type === 'net'),
    'date'
  );
  const usage: { [key: string]: string | number | Date }[] = [];
  // Start previous day as metrics are not fully calculated until the day after
  const startDate = dayjs().subtract(1, 'day');

  for (let i = 0; i <= timeFrame; i += 1) {
    const date = `${startDate
      .subtract(timeFrame - i, 'days')
      .format(YEAR_MONTH_DAY_FORMAT)}`;

    usage.push({
      date: dayjs(date).toDate(),
      usage: netUsagePerDay[date] ? netUsagePerDay[date]?.mb_per_second : 0
    });
  }

  return usage;
});

export const dashboardTimeFilterAtom = atom((get) => {
  const dashboardTimeFrame = get(dashboardTimeFrameAtom);
  if (dashboardTimeFrame === 7) {
    return 'last-07:00:00:00|C';
  }
  return `last-${dashboardTimeFrame}:00:00:00|C`;
});

export default dashboardTimeFrameAtom;
